/**
 * WelcomePage - Onboarding completion / welcome
 * MODULE: core/auth/frontend/pages/WelcomePage.tsx
 */
import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Button, Progress } from 'flowbite-react';
import { useOnboarding } from '../hooks/useOnboarding';
import { useAuth } from '../hooks/useAuth';
import { HiCheck, HiPlay, HiBookOpen, HiChat } from 'react-icons/hi';

export function WelcomePage() {
  const navigate = useNavigate();
  const { status, completeOnboarding, isLoading } = useOnboarding();
  const { user } = useAuth();
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('access_token');

  useEffect(() => {
    if (!hasToken) return;
    if (!isLoading && status?.completed) {
      navigate('/', { replace: true });
    }
  }, [status?.completed, isLoading, hasToken, navigate]);

  const firstName = (user as { display_name?: string; email?: string })?.display_name?.split(' ')[0]
    || (user as { email?: string })?.email?.split('@')[0]
    || '';

  const handleGoToDashboard = async () => {
    try {
      const result = await completeOnboarding();
      navigate(result.redirectUrl || '/', { replace: true });
    } catch {
      navigate('/', { replace: true });
    }
  };

  const steps = {
    accountCreated: false,
    passwordSet: false,
    tenantSelected: false,
    targetsSet: false,
    mfaConfigured: false,
    profileCompleted: false,
    ...status?.steps,
  };
  const progress = (Object.values(steps).filter(Boolean).length / Math.max(Object.keys(steps).length, 1)) * 100;

  if (!hasToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="max-w-md w-full mx-auto p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">Please log in to see your welcome page.</p>
          <Button color="blue" onClick={() => navigate('/login')}>Go to login</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-purple-700 text-white">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold">
            Welcome to Brazn{firstName ? `, ${firstName}` : ''}!
          </h1>
          <p className="text-blue-100 mt-2 text-lg">Your account is ready. Let&apos;s get you started.</p>
        </div>

        <Card className="mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">Onboarding progress</h3>
          <Progress progress={progress} color="blue" className="mb-4" />
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-center gap-2">
              <HiCheck className="text-green-500 shrink-0" /> Create your account
            </li>
            <li className="flex items-center gap-2">
              <HiCheck className="text-green-500 shrink-0" /> Set up your organization
            </li>
            <li className="flex items-center gap-2">
              {steps.targetsSet ? (
                <HiCheck className="text-green-500 shrink-0" />
              ) : (
                <span className="w-5" />
              )}
              <span>
                Set your sales quota —{' '}
                <Link
                  to="/crm/my-quota?onboarding=set-quota"
                  className="font-medium text-blue-600 hover:underline"
                >
                  open quota planner
                </Link>
              </span>
            </li>
            <li className="flex items-center gap-2">
              {steps.mfaConfigured ? <HiCheck className="text-green-500 shrink-0" /> : <span className="w-5" />}
              Configure security
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5" /> Complete your profile
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5" /> Invite team members
            </li>
          </ul>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="text-center">
            <HiPlay className="w-10 h-10 mx-auto text-blue-600 mb-2" />
            <h4 className="font-medium text-gray-900">Watch intro video</h4>
            <p className="text-sm text-gray-600 mt-1">Quick tour of the platform</p>
          </Card>
          <Card className="text-center">
            <HiBookOpen className="w-10 h-10 mx-auto text-blue-600 mb-2" />
            <h4 className="font-medium text-gray-900">Read documentation</h4>
            <p className="text-sm text-gray-600 mt-1">Guides and API docs</p>
          </Card>
          <Card className="text-center">
            <HiChat className="w-10 h-10 mx-auto text-blue-600 mb-2" />
            <h4 className="font-medium text-gray-900">Contact support</h4>
            <p className="text-sm text-gray-600 mt-1">We&apos;re here to help</p>
          </Card>
        </div>

        <div className="text-center">
          <Button color="blue" size="xl" onClick={handleGoToDashboard}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
